// main.ts — SPA router + state machine for the v3 EvernoteToOneNote webapp.
//
// Step machine:
//   login → upload → pick → progress → done
//
// Boot sequence:
//   1. Probe GET /auth/me. If signedIn=false → login.
//   2. If signed in, probe GET /imports/active.
//      - active terminal-or-running → jump straight to progress (resume).
//      - none → upload step.
//
// OAuth callback handling: the Worker handles GET /auth/callback (with the
// state nonce in querystring) and returns a 302 to FRONTEND_PUBLIC_URL +
// returnTo with a Set-Cookie sid=... header. From the SPA's point of view
// the callback is invisible — we just see the cookie on the next /auth/me.
// If the worker redirected back with ?auth_error=<code>, show the banner.

import type { ImportJobSummary, OneNoteNotebook, OneNoteSection } from '@api/lib/api-types';
import { api, ApiError } from './api';
import { renderLoginScreen } from './components/auth-button';
import { renderUploader } from './components/uploader';
import { renderNotebookPicker } from './components/notebook-picker';
import { renderProgress } from './components/progress';
import { renderDoneScreen } from './components/done-screen';
import { renderErrorBanner } from './components/error-banner';

export type Step = 'login' | 'upload' | 'pick' | 'progress' | 'done';

interface AppState {
  step: Step;
  user: { msUserId: string; displayName: string } | null;
  uploadId: string | null;
  filename: string | null;
  importId: string | null;
  picked: { notebook: OneNoteNotebook; section: OneNoteSection } | null;
  summary: ImportJobSummary | null;
}

const STATE: AppState = {
  step: 'login',
  user: null,
  uploadId: null,
  filename: null,
  importId: null,
  picked: null,
  summary: null,
};

// ── Pure router (exported for tests) ───────────────────────────────────────
export function nextStepFromImportSummary(s: ImportJobSummary | null): Step {
  if (!s) return 'upload';
  switch (s.state) {
    case 'queued':
    case 'validating':
    case 'in_progress':
      return 'progress';
    case 'completed':
    case 'partial':
    case 'failed':
    case 'cancelled':
      return 'done';
    default:
      return 'upload';
  }
}

// ── DOM helpers ────────────────────────────────────────────────────────────

function host(): HTMLElement {
  const h = document.getElementById('step-host');
  if (!h) throw new Error('step-host not found');
  return h;
}

function paintStepper(active: Step): void {
  const order: Step[] = ['login', 'upload', 'pick', 'progress', 'done'];
  const activeIdx = order.indexOf(active);
  document.querySelectorAll<HTMLElement>('.wizard-stepper li').forEach((li, i) => {
    li.removeAttribute('data-active');
    li.removeAttribute('data-done');
    if (i < activeIdx) li.setAttribute('data-done', 'true');
    if (i === activeIdx) li.setAttribute('data-active', 'true');
  });
}

function showSpinner(text: string): void {
  const card = document.createElement('section');
  card.className = 'card center';
  card.setAttribute('aria-busy', 'true');
  const p = document.createElement('p');
  const sp = document.createElement('span');
  sp.className = 'spinner';
  sp.setAttribute('aria-hidden', 'true');
  p.append(sp, document.createTextNode(' ' + text));
  card.append(p);
  host().replaceChildren(card);
}

// ── Step renderers ─────────────────────────────────────────────────────────

function showLogin(initialError?: ApiError | Error): void {
  STATE.step = 'login';
  paintStepper('login');

  if (initialError) {
    renderErrorBanner(host(), initialError, {
      onSignIn: () => showLogin(),
      onStartOver: () => showLogin(),
    });
    return;
  }
  renderLoginScreen(host(), {
    onError: (err) => handleError(err),
  });
}

function showUpload(): void {
  if (!STATE.user) return showLogin();
  STATE.step = 'upload';
  paintStepper('upload');
  renderUploader(host(), {
    user: STATE.user,
    onUploaded: (r) => {
      STATE.uploadId = r.uploadId;
      STATE.filename = r.filename;
      showPick();
    },
    onError: (err) => handleError(err),
    onSignOut: () => doSignOut(),
    onDeleteAccount: () => doDeleteAccount(),
  });
}

function showPick(): void {
  if (!STATE.user) return showLogin();
  if (!STATE.uploadId || !STATE.filename) return showUpload();
  STATE.step = 'pick';
  paintStepper('pick');
  renderNotebookPicker(host(), {
    uploadId: STATE.uploadId,
    filename: STATE.filename,
    onConfirmed: (importId, picked) => {
      STATE.importId = importId;
      STATE.picked = picked;
      showProgress();
    },
    onError: (err) => handleError(err),
  });
}

function showProgress(): void {
  if (!STATE.user) return showLogin();
  if (!STATE.importId) return showUpload();
  STATE.step = 'progress';
  paintStepper('progress');
  renderProgress(host(), {
    importId: STATE.importId,
    onDone: (summary) => {
      STATE.summary = summary;
      showDone();
    },
    onCancelled: (summary) => {
      STATE.summary = summary;
      showDone();
    },
    onError: (err) => handleError(err),
  });
}

function showDone(): void {
  if (!STATE.summary) return showUpload();
  STATE.step = 'done';
  paintStepper('done');
  renderDoneScreen(host(), {
    summary: STATE.summary,
    onStartOver: () => startOver(),
  });
}

function handleError(err: ApiError | Error): void {
  const apiErr = err instanceof ApiError ? err : null;
  if (apiErr?.action === 'signin' || apiErr?.code === 'NOT_AUTHENTICATED') {
    STATE.user = null;
    showLogin(err);
    return;
  }
  renderErrorBanner(host(), err, {
    onRetry: () => rerenderCurrent(),
    onStartOver: () => startOver(),
  });
}

function rerenderCurrent(): void {
  switch (STATE.step) {
    case 'login':    return showLogin();
    case 'upload':   return showUpload();
    case 'pick':     return showPick();
    case 'progress': return showProgress();
    case 'done':     return showDone();
  }
}

function startOver(): void {
  STATE.uploadId = null;
  STATE.filename = null;
  STATE.importId = null;
  STATE.picked = null;
  STATE.summary = null;
  if (STATE.user) showUpload();
  else showLogin();
}

async function doSignOut(): Promise<void> {
  try { await api.logout(); } catch { /* idempotent */ }
  STATE.user = null;
  startOver();
}

// v3.1 (2026-05-15): GDPR delete. Hard-stop UX — clear local state, drop
// the user back to step 1 with a one-shot notice. No retry on transient
// failure (the alternative is leaving the user with a phantom account).
async function doDeleteAccount(): Promise<void> {
  try {
    await api.deleteAccount();
  } catch (err) {
    handleError(err as Error);
    return;
  }
  STATE.user = null;
  STATE.uploadId = null;
  STATE.filename = null;
  STATE.importId = null;
  STATE.picked = null;
  startOver();
}

// ── Bootstrap ──────────────────────────────────────────────────────────────

async function bootstrap(): Promise<void> {
  paintStepper('login');
  showSpinner('Checking your session…');

  const params = new URLSearchParams(window.location.search);
  const authErr = params.get('auth_error');
  if (authErr) {
    // Strip the query param so refresh doesn't replay the error.
    const cleanUrl = window.location.pathname + window.location.hash;
    window.history.replaceState({}, '', cleanUrl);
  }

  let me: { authenticated: false } | { authenticated: true; msUserId: string; displayName: string };
  try {
    me = await api.me();
  } catch (err) {
    // The bootstrap probe must never crash the SPA — treat any failure as
    // "anonymous" and let the user start a fresh sign-in.
    if (authErr) {
      showLogin(toAuthErrorFromCode(authErr));
    } else {
      handleError(err as Error);
    }
    return;
  }

  if (!me.authenticated) {
    STATE.user = null;
    if (authErr) showLogin(toAuthErrorFromCode(authErr));
    else showLogin();
    return;
  }

  STATE.user = { msUserId: me.msUserId, displayName: me.displayName };

  // Resume any active import.
  try {
    const active = await api.activeImport();
    if (active.importJob) {
      STATE.importId = active.importJob.importId;
      STATE.summary = active.importJob;
      const step = nextStepFromImportSummary(active.importJob);
      if (step === 'progress') return showProgress();
      if (step === 'done') return showDone();
    }
  } catch {
    /* fall through to upload step */
  }

  showUpload();
}

function toAuthErrorFromCode(code: string): ApiError {
  const friendly: Record<string, string> = {
    STATE_INVALID: 'Your sign-in link expired or was already used. Please sign in again.',
    OAUTH_DENIED:  'Sign-in was cancelled. Try again when you’re ready.',
  };
  return new ApiError({
    code,
    message: friendly[code] || 'Sign-in did not complete. Please try again.',
    status: 0,
    retryable: true,
    action: 'signin',
  });
}

// Only auto-bootstrap when our wizard markup is actually in the DOM. This
// keeps `import './main.ts'` from running the app under jsdom (tests).
function autoBoot(): void {
  if (typeof document === 'undefined') return;
  if (!document.getElementById('step-host')) return;
  void bootstrap();
}
if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', autoBoot);
  } else {
    autoBoot();
  }
}
